import React, { useEffect, useState } from 'react'

// Logo Mark Component - Animated SVG with Motion.dev-inspired effects
export const LogoMark = ({ 
  className = "", 
  width = 144, 
  height = 79, 
  color = "currentColor",
  animate = true,
  ...props 
}) => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (animate) {
      const timer = setTimeout(() => setIsVisible(true), 100)
      return () => clearTimeout(timer)
    } else {
      setIsVisible(true)
    }
  }, [animate])

  return (
    <svg 
      width={width} 
      height={height} 
      viewBox="0 0 144.16 78.6" 
      className={`transition-all duration-700 ease-out ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'} ${className}`}
      {...props}
    >
      <defs>
        <style>
          {`
            .logo-path {
              fill: ${color};
              stroke: ${color};
              stroke-width: 0.5;
              stroke-dasharray: 1000;
              stroke-dashoffset: ${isVisible ? '0' : '1000'};
              transition: stroke-dashoffset 1.2s ease-out, fill 0.8s ease-out 0.4s;
              fill-opacity: ${isVisible ? '1' : '0'};
            }
            .logo-path-1 {
              animation-delay: 0s;
            }
            .logo-path-2 {
              animation-delay: 0.3s;
            }
          `}
        </style>
      </defs>
      <g>
        <path 
          className="logo-path logo-path-1"
          d="M0,16.51C-.02,10.1,5.17,4.9,11.58,4.9h21.32c19.57,0,33.7,19.79,33.7,37.72,0,19.02-15.65,35.98-35.55,35.98H11.73c-6.38,0-11.56-5.16-11.58-11.55L0,16.51Z"
        />
        <path 
          className="logo-path logo-path-2"
          d="M94.36,15.2l5.55,4.77c4.1,3.53,10.14,3.57,14.29.11L137.07,1.01c2.81-2.34,7.08-.35,7.08,3.32v62.45c0,6.11-4.95,11.06-11.06,11.06h-50.77c-6.11,0-11.06-4.95-11.06-11.06V25.79c0-11.95,14.02-18.38,23.08-10.59Z"
        />
      </g>
    </svg>
  )
}

// Full Logo Component - Animated SVG with Motion.dev-inspired effects
export const LogoFull = ({ 
  className = "", 
  width = 217, 
  height = 239, 
  color = "currentColor",
  animate = true,
  ...props 
}) => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (animate) {
      const timer = setTimeout(() => setIsVisible(true), 100)
      return () => clearTimeout(timer)
    } else {
      setIsVisible(true)
    }
  }, [animate])

  return (
    <svg 
      width={width} 
      height={height} 
      viewBox="0 0 217.48 239.17" 
      className={`transition-all duration-700 ease-out ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'} ${className}`}
      {...props}
    >
      <defs>
        <style>
          {`
            .logo-path {
              fill: ${color};
              stroke: ${color};
              stroke-width: 0.3;
              stroke-dasharray: 2000;
              stroke-dashoffset: ${isVisible ? '0' : '2000'};
              transition: stroke-dashoffset 1.5s ease-out, fill 0.8s ease-out 0.6s;
              fill-opacity: ${isVisible ? '1' : '0'};
            }
            .logo-path-1 { animation-delay: 0s; }
            .logo-path-2 { animation-delay: 0.2s; }
            .logo-path-3 { animation-delay: 0.4s; }
            .logo-path-4 { animation-delay: 0.6s; }
            .logo-path-5 { animation-delay: 0.8s; }
            .logo-path-6 { animation-delay: 1.0s; }
            .logo-path-7 { animation-delay: 1.2s; }
            .logo-path-8 { animation-delay: 1.4s; }
            .logo-path-9 { animation-delay: 1.6s; }
          `}
        </style>
      </defs>
      <g>
        <path 
          className="logo-path logo-path-1"
          d="M.07,13.9C.05,7.49,5.24,2.28,11.65,2.28h21.32c19.57,0,33.7,19.79,33.7,37.72,0,19.02-15.65,35.98-35.55,35.98H11.8c-6.38,0-11.56-5.16-11.58-11.55L.07,13.9Z"
        />
        <path 
          className="logo-path logo-path-2"
          d="M107.57,0c21.09,0,37.83,16.96,37.83,37.94s-16.85,38.05-37.61,38.05-38.05-17.07-38.05-37.72S86.48,0,107.57,0Z"
        />
        <path 
          className="logo-path logo-path-3"
          d="M13.82,88.04l13.53,11.64c3.1,2.67,7.67,2.7,10.81.08l14.86-12.39c5.45-4.54,13.72-.67,13.72,6.42v49.54c0,4.62-3.74,8.36-8.36,8.36H8.36c-4.62,0-8.36-3.74-8.36-8.36v-48.96c0-7.15,8.4-11.01,13.82-6.34Z"
        />
        <path 
          className="logo-path logo-path-4"
          d="M164.43,10.57l13.7,11.79c3.07,2.64,7.6,2.68,10.72.08l15.03-12.53c5.4-4.5,13.6-.66,13.6,6.37v49.77c0,4.58-3.71,8.29-8.29,8.29h-50.17c-4.58,0-8.29-3.71-8.29-8.29V16.85c0-7.09,8.32-10.91,13.7-6.28Z"
        />
        <path 
          className="logo-path logo-path-5"
          d="M77.47,116.26c0-9.08.04-20.37.08-27.36.02-3.63,2.97-6.56,6.6-6.56h46.94c3.65,0,6.61,2.96,6.6,6.61-.03,9.52-.12,27.23-.42,38.83-.33,16.31-13.59,30.11-30.22,30.11s-28.48-12.5-29.46-27.94c-.11-.87-.11-6.52-.11-13.7Z"
        />
        <path 
          className="logo-path logo-path-6"
          d="M149.85,96.7c0-7.82,6.34-14.16,14.16-14.16h23.02c11.74,0,20.87,10.65,20.87,21.85,0,5.87-1.93,11.89-5.83,16.24s-4.98,10.29-2.63,15.53l.06.13c4.2,9.37-2.65,19.96-12.92,19.96h-22.56c-7.82,0-14.16-6.34-14.16-14.16v-45.38Z"
        />
        <path 
          className="logo-path logo-path-7"
          d="M4.33,169.97c0-2.37,1.93-4.29,4.3-4.29h32.24c12.39,0,21.74,10.44,21.74,22.39,0,18.48-12.61,22.61-28.16,22.61-.01,0-.03,0-.04,0-2.34,0-4.25,1.88-4.26,4.23-.03,5.4-.04,14.58-.04,19.96,0,2.37-1.93,4.29-4.3,4.29H8.44c-2.38,0-4.3-1.93-4.3-4.31l.19-64.89Z"
        />
        <path 
          className="logo-path logo-path-8"
          d="M82.5,165.47h21.32c.99,0,1.79.8,1.79,1.79v5.03c0,.99.8,1.79,1.79,1.79h0c.99,0,1.79-.8,1.79-1.79v-5.03c0-.99.8-1.79,1.79-1.79h22.07c.99,0,1.79.8,1.79,1.79v70.12c0,.99-.8,1.79-1.79,1.79h-22.07c-.99,0-1.79-.8-1.79-1.79v-18.05c0-.99-.8-1.79-1.79-1.79h0c-.99,0-1.79.8-1.79,1.79v18.05c0,.99-.8,1.79-1.79,1.79h-21.73c-.99,0-1.8-.81-1.79-1.8l.41-70.12c0-.99.81-1.78,1.79-1.78Z"
        />
        <path 
          className="logo-path logo-path-9"
          d="M158.94,165.47h36.24c7.42,0,16.03,8.18,12.07,14.46l-12.07,19.76c-.95,1.51,0,3.25,0,5.03v25.02c0,5.21-6.07,9.43-11.28,9.43h-9.52c-5.21,0-9.43-4.22-9.43-9.43v-25.01c0-1.79-.51-3.54-1.47-5.05l-12.51-19.74c-3.98-6.28.53-14.48,7.96-14.48Z"
        />
      </g>
    </svg>
  )
}

// Default export for convenience
export default LogoMark
